import type {
  ChatMessageCollect,
  ChatMessageOption,
  QuestionnaireClarification,
  QuestionnaireTurnInput,
} from "@digital-twins/shared-types";
import {
  closeClarification,
  openClarificationOf,
  turnAnswersClarification,
} from "@digital-twins/shared-types";

interface CardMessage {
  id: string;
  role: string;
  content: string;
  options?: Array<ChatMessageOption> | null;
  collect?: ChatMessageCollect | null;
}

/** How a server card was answered, read off its clarification record. */
export interface CardAnswer {
  /** True when the record was answered; false when it was superseded. */
  answered: boolean;
  /** The chosen option, when one was. */
  option: ChatMessageOption | null;
  /** The user message that answered it; null for a UI intercept. */
  answerMessageId: string | null;
  /** Whether the card is a collect card. */
  collect: boolean;
}

/**
 * The server cards the chat is no longer waiting on, keyed by the card's
 * message id (plan S4.1). A card's answer is its record's `closedBy`: the
 * option id it chose and the message that answered it, never the content of
 * a neighbouring message. A card with no record (a transcript stored before
 * the records) is not in the map.
 */
export function answeredCardsOf(
  messages: ReadonlyArray<CardMessage>,
  clarifications: ReadonlyArray<QuestionnaireClarification>,
): Map<string, CardAnswer> {
  const byId = new Map(messages.map((message) => [message.id, message]));
  const answered = new Map<string, CardAnswer>();
  for (const record of clarifications) {
    if (record.status === "open" || !record.messageId) continue;
    const card = byId.get(record.messageId);
    if (!card || (!card.options?.length && !card.collect)) continue;
    const option = record.closedBy?.optionId
      ? card.options?.find((entry) => entry.id === record.closedBy?.optionId)
      : undefined;
    answered.set(card.id, {
      answered: record.status === "answered",
      option: option ?? null,
      answerMessageId: record.closedBy?.messageId ?? null,
      collect: Boolean(card.collect),
    });
  }
  return answered;
}

/** The card the open record asks with, or null (plan S4.1). */
export function openCardIdOf(
  clarifications: ReadonlyArray<QuestionnaireClarification>,
): string | null {
  return (
    clarifications.find((record) => record.status === "open")?.messageId ??
    null
  );
}

/** The card each answer message answered, keyed by the answer's id. */
export function cardsByAnswerMessage(
  answered: ReadonlyMap<string, CardAnswer>,
): Map<string, { cardId: string } & CardAnswer> {
  const byAnswer = new Map<string, { cardId: string } & CardAnswer>();
  for (const [cardId, answer] of answered) {
    if (answer.answerMessageId) {
      byAnswer.set(answer.answerMessageId, { cardId, ...answer });
    }
  }
  return byAnswer;
}

/**
 * The turn in flight, as far as the builder shows it (plan S4.1). Its turn
 * row owns its input: the record it names (`answers_clarification_id`), its
 * command and the option it chose. The session's send sets it from what it
 * enqueues, and a reload from the turn's snapshot. Null when no turn is in
 * flight.
 */
export interface InFlightTurn {
  /** What the turn asks, as its row stores it. */
  input: QuestionnaireTurnInput;
  /**
   * The turn's own user bubble, live or after a reload; null once the
   * transcript carries the turn's message.
   */
  messageId: string | null;
}

/**
 * The open record the in-flight turn is answering, and the records as the
 * builder shows them, with that record answered by the turn (plan S4.1).
 *
 * The record stays `open` until the turn commits, so without this a card,
 * the composer and the readiness banner would ask again for the answer the
 * user just gave. Whether the turn answers or supersedes the record is the
 * rule Nest closes it by. Nothing is written: a turn that fails or is
 * cancelled ends, and the record reads open again, its card answerable.
 *
 * Before its receipt turn goes out, a collect card's answer is the intake's
 * own write (the files upload and the assets save, which takes seconds):
 * `intakeCardId` names the card whose intake is writing. That record is being
 * answered too, by the same rule (a collect receipt answers a collect card),
 * but it is not shown answered yet: the intake keeps what it staged until its
 * turn goes out, and keeps it for a retry when the write fails.
 */
export function answerInFlightOf(
  messages: ReadonlyArray<CardMessage>,
  clarifications: Array<QuestionnaireClarification>,
  turn: InFlightTurn | null | undefined,
  intakeCardId: string | null = null,
): {
  answering: QuestionnaireClarification | null;
  clarifications: Array<QuestionnaireClarification>;
} {
  const open = openClarificationOf({ clarifications });
  const card = open
    ? messages.find((message) => message.id === open.messageId)
    : undefined;
  if (
    !open ||
    !turn ||
    turn.input.answersClarificationId !== open.id ||
    !turnAnswersClarification(turn.input.command?.kind, card)
  ) {
    const intakeAnswers =
      open !== null &&
      intakeCardId !== null &&
      intakeCardId === open.messageId &&
      turnAnswersClarification("collect_receipt", card);
    return { answering: intakeAnswers ? open : null, clarifications };
  }
  return {
    answering: open,
    clarifications:
      closeClarification(
        { clarifications },
        open.id,
        {
          turnId: null,
          messageId: turn.messageId,
          ...(turn.input.optionId ? { optionId: turn.input.optionId } : {}),
        },
        "answered",
      ).clarifications ?? [],
  };
}
